import React from 'react';
import styled from 'styled-components';
import closeIcon from '../../../../../../../../assets/icons/courses/AttendanceDetail/close-icon.svg';
import attendedIcon from '../../../../../../../../assets/icons/courses/AttendanceDetail/attended.svg';
import noAttendedIcon from '../../../../../../../../assets/icons/courses/AttendanceDetail/noAttended.svg';
import noRecordIcon from '../../../../../../../../assets/icons/courses/AttendanceDetail/noRecord.svg';
import pendingIcon from '../../../../../../../../assets/icons/courses/AttendanceDetail/pending.svg';
import { GlobalStyles } from '@/presentation/components/LightBox';
import Spinner from '@/presentation/components/Spinner';
import ErrorMessage from '@/presentation/components/Error';

interface AttendanceData {
  course: string;
  summary: {
    attended: number;
    noAttended: number;
    pending: number;
    noRecord: number;
  };
  months: {
    name: string;
    dates: { name: string; type: string }[];
  }[];
}

const Container = styled.article`
  position: fixed;
  height: 100%;
  width: 100%;
  top: 0;
  right: 0;
  background-color: #fff;
  z-index: 2;
  padding: 16px 16px 0px 16px;
  overflow: auto;
  color: #161d1f;
  .divider {
    background: #84b1cc;
    margin: 6px 0;
  }
  .divider2 {
    background: #84b1cc;
    margin: 16px 0;
  }
  @media (min-width: 640px) {
    width: 397px;
  }
`;

const CloseButton = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
  background-color: #fff;
  position: fixed;
  top: 0px;
  width: inherit;
  right: 0px;
  height: 52px;
  align-items: center;
  border-bottom: none;
  .closeIcon {
    padding: 10px;
    border-radius: 4px;
    img {
      cursor: pointer;
      height: 24px;
    }
  }
  @media (min-width: 768px) {
    border-bottom: 1px solid #7a959f;
  }
`;

const Detail = styled.div`
  margin-top: 52px;
  .title {
    font-family: Lato;
    font-size: 20px;
    font-weight: 700;
    line-height: 32px;
    text-align: left;
    width: max-content;
  }

  .subtitle {
    font-size: 16px;
    margin-bottom: 16px;
  }

  .legend {
    margin-bottom: 32px;

    .content {
      .title {
        font-family: Lato;
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
        text-align: left;
      }

      .row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
        font-family: Lato;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        text-align: left;

        .item {
          display: flex;
          align-items: center;
          min-width: 128px;

          .icon {
            margin-right: 5px;
          }

          .text {
            margin-right: 5px;
            width: 79px;
          }

          .value {
            font-family: Lato;
            font-size: 14px;
            font-weight: 700;
            line-height: 20px;
            text-align: left;
          }
        }
      }
    }
  }
`;

const Dates = styled.div`
  padding: 16px 16px 0px 16px;
  background: #ecf4f9;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 0px;
  border-bottom-left-radius: 0px;
  height: calc(120% - 270px);
  display: inline-block;
  width: 100%;

  .month {
    margin-bottom: 0px;

    .title {
      font-family: Lato;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      text-align: left;
      margin-bottom: 12px;
    }

    .content {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .date {
        display: flex;
        align-items: center;
        padding: 8px;
        border-radius: 4px;
        background: #fff;
        box-shadow: 0 1.2px 4px rgba(0, 0, 0, 0.1);
        text-align: center;
        flex-direction: column;
        width: 59px;
        height: 64px;

        .title {
          font-family: Lato;
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          text-align: center;
          margin-bottom: 4.5px;
        }
      }
    }
  }
  @media (min-height: 480px) {
    height: calc(120% - 270px);
  }
  @media (min-height: 1000px) {
    height: calc(120vh - 270px);
  }
`;

interface Props {
  attendanceData: AttendanceData;
  setShowAttendance: (show: boolean) => void;
  courseTitle: string;
  isLoading?: boolean;
  error: string;
}

const AttendanceDetailView: React.FC<Props> = ({
  attendanceData,
  setShowAttendance,
  courseTitle,
  isLoading = false,
  error
}) => (
  <Container data-testid="cmp-attendance-detail">
    <GlobalStyles />
    <CloseButton
      onClick={() => setShowAttendance(false)}
      data-testid="closeIcon"
    >
      <div className="closeIcon">
        <img src={closeIcon} alt="close" />
      </div>
    </CloseButton>
    <Detail>
      <div className="title" style={{ fontSize: 20 }}>
        Detalle de asistencia
      </div>
      <div className="subtitle">Curso: {courseTitle}</div>
      {isLoading || !attendanceData ? (
        <Spinner />
      ) : (
        <>
          {error ||
            (attendanceData.months.length === 0 ? (
              <ErrorMessage />
            ) : (
              <div>
                <div className="legend">
                  <div className="content">
                    <div className="title">Leyenda:</div>
                    <hr className="divider" />
                    <div className="row">
                      <div className="item">
                        <div className="icon">
                          <img src={attendedIcon} alt="attended" />
                        </div>
                        <div className="text">Asistió:</div>
                        <div className="value">
                          {attendanceData.summary.attended}
                        </div>
                      </div>
                      <div className="item">
                        <div className="icon">
                          <img src={noAttendedIcon} alt="noAttended" />
                        </div>
                        <div className="text">No asistió:</div>
                        <div className="value">
                          {attendanceData.summary.noAttended}
                        </div>
                      </div>
                    </div>
                    <hr className="divider" />
                    <div className="row">
                      <div className="item">
                        <div className="icon">
                          <img src={pendingIcon} alt="pending" />
                        </div>
                        <div className="text">Pendiente:</div>
                        <div className="value">
                          {attendanceData.summary.pending}
                        </div>
                      </div>
                      <div className="item">
                        <div className="icon">
                          <img src={noRecordIcon} alt="noRecord" />
                        </div>
                        <div className="text">Sin registro:</div>
                        <div className="value">
                          {attendanceData.summary.noRecord}
                        </div>
                      </div>
                    </div>
                    <hr className="divider" />
                  </div>
                </div>
                <Dates data-testid="cmp-attendance-dates">
                  {attendanceData.months.map((month, index) => (
                    <div className="month" key={index}>
                      <div className="title">{month.name}</div>
                      <div className="content">
                        {month.dates.map((date, idx) => (
                          <div className="date" key={idx}>
                            <div className="title">{date.name}</div>
                            <div className="icon">
                              {date.type === 'P' && (
                                <img src={pendingIcon} alt="pending" />
                              )}
                              {date.type === 'A' && (
                                <img src={attendedIcon} alt="attended" />
                              )}
                              {date.type === 'NA' && (
                                <img src={noAttendedIcon} alt="noAttended" />
                              )}
                              {date.type === 'NR' && (
                                <img src={noRecordIcon} alt="noRecord" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {index !== attendanceData.months.length - 1 && (
                        <hr className="divider2" />
                      )}
                    </div>
                  ))}
                  <div style={{ height: 100 }} />
                </Dates>
              </div>
            ))}
        </>
      )}
    </Detail>
  </Container>
);

export default AttendanceDetailView;
